import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { RoleService } from '../../services/role.service';
import { IRole, IRoleSummary } from '../../interfaces/role.interface';
import { useToast } from '../../context/ToastContext';
import { useIsAdmin } from '../../hooks/usePermission';

interface Props {
    open: boolean;
    institutionId: string;
    user: { id: string; username: string; roles: IRoleSummary[] } | null;
    onClose: () => void;
    onSaved: () => void;
}

const ChangeRolesDialog: React.FC<Props> = ({ open, institutionId, user, onClose, onSaved }) => {
    const toast = useToast();
    const isAdmin = useIsAdmin();
    const [all, setAll] = useState<IRole[]>([]);
    const [value, setValue] = useState<IRole[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !institutionId) return;
        RoleService.list(institutionId).then(rs => {
            const active = rs.filter(r => r.isActive);
            setAll(active);
            setValue(active.filter(r => user?.roles.some(ur => ur.id === r.id)));
        }).catch(e => toast.showApiError(e, 'Failed to load roles'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, institutionId, user]);

    const options = useMemo(() => all.filter(r => isAdmin || !r.isAdmin), [all, isAdmin]);

    const save = async () => {
        if (!user) return;
        setSaving(true);
        try {
            await RoleService.setUserRoles(institutionId, user.id, value.map(r => r.id));
            toast.showSuccess('Roles updated');
            onSaved();
        } catch (e) {
            toast.showApiError(e, 'Failed to update roles');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-help-page="users.changeRoles.page">
            <DialogTitle>Change roles — {user?.username}</DialogTitle>
            <DialogContent>
                <Autocomplete
                    data-help="users.changeRoles.field.roles"
                    multiple
                    options={options}
                    value={value}
                    onChange={(_, v) => setValue(v)}
                    getOptionLabel={r => r.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderTags={(tags, getTagProps) => tags.map((r, i) => (
                        <Chip {...getTagProps({ index: i })} key={r.id} label={r.name} deleteIcon={<span aria-label={`Remove ${r.name}`}>×</span>} />
                    ))}
                    renderInput={params => <TextField {...params} label="Roles" margin="normal" />}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} data-help="users.changeRoles.cancel">Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving} data-help="users.changeRoles.save">Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeRolesDialog;
